import { mediaPreview } from '../lib/mediaDelivery';
import { createPortal } from 'react-dom';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FiArrowRight, FiChevronLeft, FiChevronRight, FiX } from 'react-icons/fi';
import Tv from './Tv';
import ProfileDetails from './ProfileDetails';
import HeaderNavigation from './HeaderNavigation';
import Header from './Header';
import useSwipeFeed from '../lib/useSwipeFeed';
import { useSeenPosts } from '../lib/feedPreferences';
import UnseenPost from './UnseenPost';

function shuffled(values) {
  const result = [...values];
  for (let index = result.length - 1; index > 0; index--) {
    const other = Math.floor(Math.random() * (index + 1));
    [result[index], result[other]] = [result[other], result[index]];
  }
  return result;
}

export default function PostStories({ posts, loading, previewOnly = false, feedSource }) {
  const seenHistory = useSeenPosts();
  const seenPosts = new Set(seenHistory);
  const [browsingPreviews, setBrowsingPreviews] = useState(false);
  const strip = useRef(null);
  const previewRequest = useRef(-1);
  const [previewIds, setPreviewIds] = useState([]);
  const [viewerIds, setViewerIds] = useState([]);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [settling, setSettling] = useState(false);
  const slideTimer = useRef(null);
  const viewport = useRef(null);
  const [waiting, setWaiting] = useState(false);
  const [active, setActive] = useState(null);
  const dialog = useRef(null);
  const touch = useRef(null);
  const opener = useRef(null);
  const requested = useRef(-1);
  const isOpen = active !== null;
  const ownReel = useSwipeFeed(!feedSource && (isOpen || browsingPreviews));
  const reel = feedSource || ownReel;
  const { loadMore: loadMoreReel } = reel;
  const viewerPool = [...new Map([...posts, ...reel.posts].map(item => [item.post_id, item])).values()];
  const postsById = new Map(viewerPool.map(item => [item.post_id, item]));
  const previewPosts = previewIds.map(id => postsById.get(id)).filter(item => item && !seenPosts.has(String(item.post_id)));
  useEffect(() => {
    setPreviewIds(previous => {
      const known = new Set(previous);
      const incoming = [...new Map([...posts, ...reel.posts].map(item => [item.post_id, item])).values()].filter(item => !known.has(item.post_id)).map(item => item.post_id);
      return incoming.length ? [...previous, ...shuffled(incoming)] : previous;
    });
  }, [posts, reel.posts]);
  const viewerById = new Map(viewerPool.map(item => [item.post_id, item]));
  const viewerPosts = viewerIds.map(id => viewerById.get(id)).filter(Boolean);
  const post = active === null ? null : viewerPosts[active];
  useEffect(() => {
    if (!isOpen) return;
    setViewerIds(previous => {
      const known = new Set(previous);
      const incoming = previewIds.filter(id => !known.has(id) && !seenHistory.includes(String(id)));
      return incoming.length ? [...previous, ...incoming] : previous;
    });
  }, [previewIds, isOpen, seenHistory]);
  const canSwipe = (target, y) => !target.closest('button, a, input, textarea, .carousel-navigation, [role="dialog"] [role="dialog"]') && (target.tagName !== 'VIDEO' || y < target.getBoundingClientRect().bottom - 48);
  const open = (index, event) => {
    opener.current = event.currentTarget;
    setViewerIds(previewPosts.map(item => item.post_id));
    setWaiting(false);
    setActive(index ?? 0);
  };
  const close = () => { clearTimeout(slideTimer.current); setSwipeOffset(0); setSettling(false); setWaiting(false); setActive(null); requestAnimationFrame(() => opener.current?.focus()); };
  const move = direction => {
    if (direction > 0 && active === viewerPosts.length - 1 && reel.hasMore) { setWaiting(true); if (!reel.loading && !reel.error) reel.loadMore(); return; }
    setWaiting(false);
    setActive(index => Math.max(0, Math.min(viewerPosts.length - 1, index + direction)));
  };
  useEffect(() => () => clearTimeout(slideTimer.current), []);
  const drag = (x, y) => {
    const start = touch.current;
    if (!start || settling) return;
    const dx = x - start.x;
    if (Math.abs(dx) < Math.abs(y - start.y)) return;
    const width = viewport.current?.clientWidth || 360;
    const edge = dx > 0 ? active === 0 : active === viewerPosts.length - 1;
    setSwipeOffset(Math.max(-width, Math.min(width, edge ? dx * .2 : dx)));
  };
  const finishSwipe = (x, y) => {
    const start = touch.current;
    touch.current = null;
    if (!start || settling) return;
    const dx = x - start.x;
    if (Math.abs(dx) <= 50 || Math.abs(dx) <= Math.abs(y - start.y) * 1.2) { setSwipeOffset(0); return; }
    const direction = dx < 0 ? 1 : -1;
    if (!viewerPosts[active + direction]) { setSwipeOffset(0); move(direction); return; }
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) { setSwipeOffset(0); move(direction); return; }
    setSettling(true);
    setSwipeOffset(direction * -(viewport.current?.clientWidth || 360));
    slideTimer.current = setTimeout(() => { move(direction); setSwipeOffset(0); setSettling(false); }, 180);
  };
  const navigateDesktop = direction => {
    if (settling) return;
    touch.current = { x: 0, y: 0 };
    finishSwipe(-direction * 100, 0);
  };
  const progressStart = Math.floor((active || 0) / 5) * 5;
  const progressLength = reel.hasMore ? 5 : Math.min(5, viewerPosts.length - progressStart);
  const cancelSwipe = () => { touch.current = null; setSwipeOffset(0); };
  const neighbor = direction => {
    const item = viewerPosts[active + direction];
    const media = item?.media?.find(part => /video/i.test(part.file_type || '')) || item?.media?.[0] || item;
    return <div className="reel-adjacent" aria-hidden="true"><div className="film-frame">{media && (/video/i.test(media.file_type || '') || /\.(mp4|webm|mov)(\?|$)/i.test(media.file_link || '') ? <video src={media.file_link} poster={mediaPreview(media.file_link, 320) !== media.file_link ? mediaPreview(media.file_link, 320) : undefined} muted playsInline preload="none" /> : <img src={mediaPreview(media.file_link, 320)} alt="" loading="lazy" />)}</div></div>;
  };
  useEffect(() => {
    if (waiting && active !== null && active < viewerPosts.length - 1) {
      setActive(value => value + 1);
      setWaiting(false);
    } else if (!reel.loading && !reel.hasMore) setWaiting(false);
  }, [waiting, viewerPosts.length, active, reel.loading, reel.hasMore]);
  useEffect(() => {
    if (!isOpen) return;
    dialog.current?.focus();
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = overflow; };
  }, [isOpen]);
  useEffect(() => {
    if (active === null || active < viewerPosts.length - 4 || !reel.hasMore || reel.loading || reel.error || requested.current === reel.page) return;
    requested.current = reel.page;
    loadMoreReel();
  }, [active, viewerPosts.length, reel.hasMore, reel.loading, reel.error, reel.page, loadMoreReel]);
  const startPreviewBrowse = () => {
    setBrowsingPreviews(true);
    const row = strip.current;
    if (!row || row.scrollWidth - row.clientWidth > 180 || !reel.hasMore || reel.loading || reel.error || previewRequest.current === reel.page) return;
    previewRequest.current = reel.page;
    loadMoreReel();
  };
  const loadPreviews = useCallback(() => {
    const row = strip.current;
    if (!row || row.scrollLeft <= 0 || row.scrollWidth - row.clientWidth - row.scrollLeft > 180) return;
    setBrowsingPreviews(true);
    if (!reel.hasMore || reel.loading || reel.error || previewRequest.current === reel.page) return;
    previewRequest.current = reel.page;
    loadMoreReel();
  }, [reel.hasMore, reel.loading, reel.error, reel.page, loadMoreReel]);
  useEffect(() => { if (browsingPreviews) loadPreviews(); }, [browsingPreviews, previewIds.length, loadPreviews]);
  return <div className="reference-feed">
    <section className="post-stories" aria-label="Explore meme posts">
      <div className="stories-strip" ref={strip} onScroll={loadPreviews} onTouchStart={startPreviewBrowse} onPointerDown={startPreviewBrowse} onWheel={startPreviewBrowse} aria-label="Browse post previews" aria-busy={browsingPreviews && reel.loading}>
        <button type="button" className="story-preview explore-preview" disabled={!previewPosts.length} onClick={event => previewPosts.length && open(null, event)} aria-label="Explore posts"><span className="story-ring"><span>Explore<FiArrowRight /></span></span></button>
        {previewPosts.map((item, previewIndex) => {
          const index = viewerPool.findIndex(candidate => candidate.post_id === item.post_id);
          const media = item.media?.[0] || item;
          const video = /video/i.test(media.file_type || '') || /\.(mp4|webm|mov)(\?|$)/i.test(media.file_link || '');
          return <button type="button" className="story-preview" key={item.post_id} onClick={event => open(previewIndex, event)} aria-label={`Open post ${index + 1} in status view`}>
            <span className="story-ring">{video ? <video src={media.file_link} poster={mediaPreview(media.file_link, 120) !== media.file_link ? mediaPreview(media.file_link, 120) : undefined} muted playsInline preload="none" aria-hidden="true" /> : <img src={mediaPreview(media.file_link, 120)} alt="" loading="lazy" onError={event => { event.currentTarget.style.visibility = 'hidden'; }} />}</span>
          </button>;
        })}
        {((loading && posts.length === 0) || (browsingPreviews && reel.loading)) && Array.from({ length: browsingPreviews ? 2 : 5 }, (_, index) => <span className="story-placeholder" key={index} aria-hidden="true" />)}
        {browsingPreviews && reel.error && <button type="button" className="preview-retry" onClick={reel.retry}>Try again</button>}
      </div>
    </section>
    {!previewOnly && <><Header searchOnly />
    <HeaderNavigation />
    <div className="discover-posts">
      {posts.map((item, index) => <UnseenPost seen={seenPosts.has(String(item.post_id))} id={`feed-post-${item.post_id}`} key={item.post_id}>
        <ProfileDetails user_id={item.user_id} profile_picture_link={item.profile_picture_link} userName={item.username} date_time={item.date_time} post_id={item.post_id} email={item.email} />
        <Tv {...item} priority={index === 0} views={item.view_count} />
      </UnseenPost>)}
    </div></>}
    {post && createPortal(<div className="story-backdrop" onClick={event => { if (event.target === event.currentTarget) close(); }}>
      <section className="story-viewer post-status-viewer" ref={dialog} role="dialog" aria-modal="true" aria-label="Post status viewer" tabIndex={-1}
        onKeyDown={event => {
          if (event.key === 'Escape') { event.stopPropagation(); close(); }
          if (event.target === event.currentTarget && ['ArrowLeft', 'ArrowRight'].includes(event.key)) { event.preventDefault(); move(event.key === 'ArrowRight' ? 1 : -1); }
          if (event.key === 'Tab') {
            const controls = Array.from(dialog.current.querySelectorAll('button:not(:disabled), a[href], input, textarea, [tabindex="0"]')).filter(element => element.getClientRects().length);
            const first = controls[0], last = controls[controls.length - 1];
            if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog.current)) { event.preventDefault(); last?.focus(); }
            else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
          }
        }}>
        <div className="reel-mobile-progress" aria-hidden="true">{Array.from({ length: Math.max(0, progressLength) }, (_, index) => <span key={progressStart + index} className={progressStart + index < active ? 'is-complete' : progressStart + index === active ? 'is-current' : undefined} />)}</div>
        <button type="button" className="reel-desktop-arrow reel-desktop-previous" aria-label="Previous post" disabled={active === 0 || settling} onClick={() => navigateDesktop(-1)}><FiChevronLeft aria-hidden="true" /></button>
        <button type="button" className="reel-desktop-arrow reel-desktop-next" aria-label="Next post" disabled={settling || (!reel.hasMore && active === viewerPosts.length - 1) || (waiting && reel.loading)} onClick={() => navigateDesktop(1)}><FiChevronRight aria-hidden="true" /></button>
        <div className="story-viewer-heading"><div className="status-author">{post.profile_picture_link && <img src={post.profile_picture_link} alt="" />}<div><strong>{post.username || 'Happy Times Memes'}</strong><small>Happy Times Memes</small></div></div><button type="button" onClick={close} aria-label="Close post viewer"><FiX /></button></div>
        <div className="reel-viewport" ref={viewport}>
        <div className={`reel-track${settling ? ' is-settling' : ''}`} style={{ transform: `translateX(calc(-100% + ${swipeOffset}px))` }}>
        {neighbor(-1)}
        <div className="post-status-stage" onTouchStartCapture={event => {
          if (settling || event.touches.length !== 1 || !canSwipe(event.target, event.touches[0].clientY)) { touch.current = null; return; }
          touch.current = { x: event.touches[0].clientX, y: event.touches[0].clientY };
        }} onTouchMoveCapture={event => { if (event.touches.length === 1) drag(event.touches[0].clientX, event.touches[0].clientY); }} onPointerDownCapture={event => {
          if (settling || event.pointerType !== 'mouse' || event.button !== 0 || !canSwipe(event.target, event.clientY)) return;
          touch.current = { x: event.clientX, y: event.clientY };
          event.currentTarget.setPointerCapture?.(event.pointerId);
        }} onPointerMoveCapture={event => { if (event.pointerType === 'mouse') drag(event.clientX, event.clientY); }} onPointerUpCapture={event => {
          if (!touch.current || event.pointerType !== 'mouse') return;
          if (Math.abs(event.clientX - touch.current.x) > 50) event.stopPropagation();
          finishSwipe(event.clientX, event.clientY);
        }} onPointerCancel={cancelSwipe} onTouchCancel={cancelSwipe} onTouchEndCapture={event => {
          if (!touch.current) return;
          const point = event.changedTouches[0];
          if (Math.abs(point.clientX - touch.current.x) > 50) event.stopPropagation();
          finishSwipe(point.clientX, point.clientY);
        }}>
          <Tv key={post.post_id} {...post} preferVideo views={post.view_count} />
        </div>
        {neighbor(1)}
        </div>
        </div>
        <p className="reel-mobile-cue">Swipe left or right to explore</p>
        <span className="viewer-announcement" aria-live="polite">{waiting && reel.loading ? 'Loading the next post' : post.username ? `Post by ${post.username}` : 'Meme post'}</span>
        {reel.loading && waiting && <div className="reel-loading" role="status">Finding your next laugh…</div>}
        {reel.error && <button type="button" className="reel-retry" onClick={reel.retry}>Retry loading posts</button>}
        {!reel.hasMore && active === viewerPosts.length - 1 && <p className="reel-end">You’re all caught up. More laughs are on the way.</p>}
      </section>
    </div>, document.body)}
  </div>;
}
